import { ChangeDetectionStrategy, AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { TenantInfoService, TenantService } from '@vsolv/core/multi-tenant/web';
import { UserService } from '@vsolv/core/users/web';
import { FirebaseService } from '@vsolv/packages/firebase/web';
import { Config } from '@vsolv/packages/portal-config/domain';
import { PortalService } from '@vsolv/packages/portal-config/web';
import { CustomersCustomerPortalService, PaymentsCustomerPortalService } from '@wsphere/customer-portal/web';
import { Policy } from '@wsphere/warranties/domain';
import { PolicyService, WarrantyWebService } from '@wsphere/warranties/web';
import { Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, takeUntil } from 'rxjs/operators';
import { SalesService } from '../services/sales.service';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './layout.component.html',
  standalone: false,
})
export class CustomerPortalLayout implements OnDestroy, OnInit, AfterViewInit {
  constructor(
    private router: Router,
    private titleSvc: Title,
    private route: ActivatedRoute,
    public portalSvc: PortalService,
    private tenantSvc: TenantService,
    private tenantInfoSvc: TenantInfoService,
    private firebaseSvc: FirebaseService,
    private paymentSvc: PaymentsCustomerPortalService,
    private customerPortalSvc: CustomersCustomerPortalService,
    private policySvc: PolicyService,
    private warrantySvc: WarrantyWebService,
    private userSvc: UserService,
    private salesSvc: SalesService
  ) {
    this.router.events
      .pipe(
        takeUntil(this.onDestroy$),
        filter(e => e instanceof NavigationEnd)
      )
      .subscribe(async e => {
        this.drawerOpen = (e as NavigationEnd).url.split('/').length === 2;
        await this.refreshImpersonationBanner();

        // Refresh sales disabled status on navigation to ensure latest value
        // This helps when admin changes settings while user is browsing
        this.salesDisabled = await this.salesSvc.getSalesDisabled();
      });
  }

  drawerOpen = true;
  impersonationBanner = { visible: false, customerId: '', customerName: '' };
  private readonly impersonationStorageKey = 'portalImpersonationContext';

  private onDestroy$ = new Subject<void>();

  readonly config$ = this.route.data.pipe(map(data => data['config'] as Config.PortalConfig));

  // Sales disabled status - fetched when needed
  salesDisabled = false;

  isSignedIn$ = this.firebaseSvc.getAuthId$().pipe(
    distinctUntilChanged(),
    map(id => !!id)
  );

  hasPayments$ = this.isSignedIn$.pipe(
    switchMap(async isSignedIn => {
      if (!isSignedIn) return false;

      const payments = (await this.paymentSvc.getList({ limit: 1 })).items;

      return !!payments.length;
    })
  );

  policiesAvailable$ = this.isSignedIn$.pipe(
    switchMap(async isSignedIn => {
      if (!isSignedIn) return false;

      const policies = (await this.policySvc.listForSales({ limit: 100 }, undefined, Policy.Status.PUBLISHED)).items;
      return !!policies.filter(
        policy =>
          policy.terms.some(term => term.paymentSchedules.length > 0) && policy.plans?.some(plan => plan.visible)
      ).length;
    })
  );

  hasWarranties$ = this.firebaseSvc.authUser$.pipe(
    switchMap(async isSignedIn => {
      if (isSignedIn) {
        const user = await this.userSvc.getSelf();
        if (user && user.email) {
          return (await this.warrantySvc.getOneByCustomerEmail(user.email)) ? true : false;
        } else return false;
      } else return false;
    })
  );

  open = false;

  tenantId: string | null = null;

  ngOnDestroy() {
    this.onDestroy$.next();
  }

  ngAfterViewInit() {
    this.config$.pipe(takeUntil(this.onDestroy$)).subscribe(config => {
      this.titleSvc.setTitle(config.brandName + ' Portal');

      const favicon = document.querySelector<HTMLLinkElement>('#favicon');
      if (favicon && config.avatar) favicon.href = config.avatar;
    });
  }

  async ngOnInit() {
    this.tenantId = this.tenantSvc.tenantId || this.tenantInfoSvc.getLocalTenantId();
    await this.refreshImpersonationBanner();

    // Fetch sales disabled status for the button
    this.salesDisabled = await this.salesSvc.getSalesDisabled();
  }

  async signedOut() {
    await this.router.navigate(['login']);
  }

  checkLink(link: string) {
    return this.router.url.indexOf(link) > 0;
  }

  private async refreshImpersonationBanner() {
    const query = this.router.parseUrl(this.router.url).queryParams;
    const queryCustomerId = (query['impersonatedCustomerId'] as string) || '';
    const queryCustomerName = (query['impersonatedCustomerName'] as string) || '';
    const isImpersonating = query['impersonating'] === '1' || !!queryCustomerId || !!queryCustomerName;

    if (isImpersonating) {
      const previousContext = this.getStoredImpersonationContext();
      const customerChanged = !!queryCustomerId && previousContext?.customerId !== queryCustomerId;
      const context: { visible: boolean; customerId: string; customerName: string } = {
        visible: true,
        customerId: queryCustomerId || previousContext?.customerId || '',
        // When customer id changes but name is missing from query, avoid showing stale previous name.
        customerName:
          queryCustomerName || (customerChanged ? 'this customer' : previousContext?.customerName || 'this customer'),
      };

      // Keep banner in sync with the active signed-in customer after redirects/refreshes.
      try {
        const currentCustomer = await this.customerPortalSvc.retrieveSelf();
        if (currentCustomer?.id) {
          context.customerId = currentCustomer.id;
          context.customerName = currentCustomer.name || context.customerName;
        }
      } catch {
        // Best effort only; keep query/storage value when self profile retrieval fails.
      }

      sessionStorage.setItem(this.impersonationStorageKey, JSON.stringify(context));
      this.impersonationBanner = context;
      return;
    }

    const storedContext = this.getStoredImpersonationContext();
    if (storedContext?.visible) {
      const context = { ...storedContext };

      // Query params can be dropped during auth redirects; rehydrate with current customer.
      try {
        const currentCustomer = await this.customerPortalSvc.retrieveSelf();
        if (currentCustomer?.id) {
          context.customerId = currentCustomer.id;
          context.customerName = currentCustomer.name || context.customerName || 'this customer';
          sessionStorage.setItem(this.impersonationStorageKey, JSON.stringify(context));
        }
      } catch {
        // Keep stored context if self profile call is temporarily unavailable.
      }

      this.impersonationBanner = context;
      return;
    }

    this.impersonationBanner = { visible: false, customerId: '', customerName: '' };
  }

  private getStoredImpersonationContext(): { visible: boolean; customerId: string; customerName: string } | null {
    const stored = sessionStorage.getItem(this.impersonationStorageKey);
    if (!stored) return null;

    try {
      const parsed = JSON.parse(stored) as { visible?: boolean; customerId?: string; customerName?: string };
      return {
        visible: !!parsed.visible,
        customerId: parsed.customerId || '',
        customerName: parsed.customerName || '',
      };
    } catch {
      return null;
    }
  }
}
